import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Offer } from "@/services/backend/qpm/offers/types/offer";
import {
  resetField,
  rtkErrIsValidationError,
  ValidationError,
} from "@/shared/lib/errors/validation-error";
import { useDebounce } from "@/shared/lib/debounce/debounce";
import { useQpmOfferPutMutation } from "@/services/backend/qpm/offers/api/offer-api";

interface OfferContextInterface {
  offer: Offer;
  onOfferChange: (offer: Offer) => void;
  validationError: ValidationError | null;
  setValidationError: (err: ValidationError | null) => void;
  resetFieldError: (field: string) => void;
}

export const OfferContext = createContext<OfferContextInterface>({
  offer: {} as Offer,
  onOfferChange: () => null,
  validationError: null,
  setValidationError: () => null,
  resetFieldError: () => null,
});

export function useOfferContext() {
  return useContext(OfferContext);
}

export function OfferContextProvider({
  children,
  offer: propOffer,
}: {
  children: ReactNode;
  offer: Offer;
}) {
  const [offer, setOffer] = useState<Offer>(propOffer);
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);

  // Match HTZ context behavior: when switching to a different offer, update state immediately
  // (and clear stale validation errors) during render.
  if (offer.id !== propOffer.id) {
    setOffer(propOffer);
    setValidationError(null);
  }

  const onOfferChange = (changedOffer: Offer) => {
    setOffer(changedOffer);
  };

  const resetFieldError = (field: string) => {
    setValidationError((curr) => resetField(curr, field));
  };

  // Persist (debounced) like other document flows.
  const [putOffer, { error: putError }] = useQpmOfferPutMutation();
  const debouncedOffer = useDebounce(offer, 500);

  // Prevent the initial prop->state sync from triggering an immediate PUT.
  const lastPersistedOfferId = useRef<string | null>(null);
  const lastPersistedJson = useRef<string | null>(null);

  useEffect(() => {
    if (!debouncedOffer) {
      return;
    }

    // Don't persist immutable offers.
    if (debouncedOffer.immutable) {
      return;
    }

    // If offerId changes, reset tracking to avoid persisting old snapshot.
    if (lastPersistedOfferId.current !== debouncedOffer.id) {
      lastPersistedOfferId.current = debouncedOffer.id;
      lastPersistedJson.current = JSON.stringify(debouncedOffer);
      return;
    }

    const json = JSON.stringify(debouncedOffer);
    if (json === lastPersistedJson.current) {
      return;
    }

    lastPersistedJson.current = json;
    putOffer(debouncedOffer);
  }, [debouncedOffer, putOffer]);

  useEffect(() => {
    if (!rtkErrIsValidationError(putError)) {
      return;
    }

    setValidationError(putError.data);
  }, [putError]);

  const value = useMemo(
    () => ({
      offer,
      onOfferChange,
      validationError,
      setValidationError,
      resetFieldError,
    }),
    [offer, validationError],
  );

  return (
    <OfferContext.Provider value={value}>{children}</OfferContext.Provider>
  );
}
